import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useSnapshot } from 'valtio';
import '@wangeditor/editor/dist/css/style.css';
import {
  deleteCouponApi,
  getCouponDetailApi,
  getCouponReportApi,
  getFreeSelfCouponApi,
  queryCouponListApi,
  savePlatformCouponApi,
  sendBatchCouponApi,
  updateCouponByIdApi,
} from '@/api/pointCenter/storeCoupon';
import ImageUploader from '@/component/ImageUploader';
import { COUPON_KEY } from '@/router/Permissions';
import { state } from '@/store';
import { defaultEditorConfig, defaultToolbarConfig } from '@/utils/editorConfig';
import { checkPermissions } from '@/utils/utils';
import styles from './index.module.scss';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const EMPTY_HTML = '<p><br></p>';
const isEmptyHtml = (html: string) => {
  if (!html) return true;
  if (/<img\b[^>]*\bsrc\s*=\s*['"][^'"]+['"][^>]*>/i.test(html)) {
    return false;
  }
  const plain = html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return !plain;
};

const StoreCoupon: React.FC = () => {
  const [form] = Form.useForm();
  const [queryForm] = Form.useForm();
  const { currentShopId, currentShopPlatformType, currentShopIsPop } = useSnapshot(state);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const [detailHtml, setDetailHtml] = useState<string>(EMPTY_HTML);
  const [editorKey, setEditorKey] = useState(0);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [reportVisible, setReportVisible] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [reportData, setReportData] = useState<any>(null);
  const [sendVisible, setSendVisible] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendTarget, setSendTarget] = useState<any>(null);
  const [sendAvailableNum, setSendAvailableNum] = useState<number | null>(null);
  const [sendForm] = Form.useForm();

  const toolbarConfig = useMemo<Partial<IToolbarConfig>>(() => defaultToolbarConfig, []);
  const editorConfig = useMemo<Partial<IEditorConfig>>(() => defaultEditorConfig, []);

  const validityType = Form.useWatch('validityType', form);
  const bindType = Form.useWatch('bindType', form);
  const takeRule = Form.useWatch('takeRule', form);
  const couponType = Form.useWatch('type', form);
  const isPopShop = currentShopIsPop === 1;
  const isEdit = editingId !== null;
  const editableFields = useMemo(() => {
    if (!isEdit) return null;
    if (isPopShop) {
      return new Set(['name', 'thumbnail', 'mainImage', 'detail']);
    }
    return new Set(['name', 'style', 'bindType', 'bindSkuIds', 'thumbnail', 'mainImage', 'detail']);
  }, [isEdit, isPopShop]);
  const isFieldDisabled = (key: string) => (isEdit && editableFields ? !editableFields.has(key) : false);
  const infoCardClassName = [styles.infoCard, isPopShop ? styles.popShopInfoCard : ''].filter(Boolean).join(' ');

  useEffect(() => {
    if (currentShopId) {
      fetchList({ pageNum: 1 });
    } else {
      setDataSource([]);
      setTotal(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  useEffect(() => {
    if (!drawerVisible && editor) {
      editor.destroy();
      setEditor(null);
    }
  }, [drawerVisible, editor]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
        setEditor(null);
      }
    };
  }, [editor]);

  const openDrawer = () => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      style: currentShopPlatformType === 'TM' ? 4 : 1,
      bindType: 1,
      type: 1,
      validityType: isPopShop ? 1 : undefined,
      takeRule: 0,
    });
    setDetailHtml(EMPTY_HTML);
    setEditorKey(prev => prev + 1);
    setDrawerVisible(true);
  };

  const handleClose = () => {
    setDrawerVisible(false);
    setEditingId(null);
    form.resetFields();
    setDetailHtml(EMPTY_HTML);
    setEditorKey(prev => prev + 1);
  };

  const handleFetchCouponInfo = async () => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    if (isEdit) return;
    if (isPopShop) return;
    try {
      const putKey = form.getFieldValue('putKey');
      if (!putKey) {
        message.warning('请先输入putKey');
        return;
      }
      setQueryLoading(true);
      const res = await getFreeSelfCouponApi(currentShopId, putKey);
      if (res?.code === 200) {
        const info = res.data || {};
        const takeTime =
          info.activityStartTime && info.activityEndTime
            ? [dayjs(info.activityStartTime), dayjs(info.activityEndTime)]
            : undefined;
        const validityRange = info.beginTime && info.endTime ? [dayjs(info.beginTime), dayjs(info.endTime)] : undefined;
        const existingTakeRule = form.getFieldValue('takeRule');
        const existingTakeNum = form.getFieldValue('takeNum');
        const hasTakeRule = info.takeRule !== undefined && info.takeRule !== null;
        const hasTakeNum = info.takeNum !== undefined && info.takeNum !== null;
        form.setFieldsValue({
          takeTime,
          validityType: info.expireType,
          days: info.addDays,
          mainDiscount: info.discount,
          mainQuota: info.quota,
          allCount: info.allCount ?? info.batchCount,
          validityRange,
          batchKey: info.batchKey,
          takeRule: hasTakeRule ? info.takeRule : hasTakeNum ? 3 : existingTakeRule,
          takeNum: hasTakeNum ? info.takeNum : existingTakeNum,
          putKey,
        });
        message.success('优惠券信息已获取');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    try {
      const values = await form.validateFields();
      if (!values.takeTime || values.takeTime.length !== 2) {
        message.error(isPopShop ? '请选择领取时间' : '请先通过 putKey 查询并获取领取时间');
        return;
      }
      if (!values.validityType && currentShopPlatformType !== 'TM') {
        message.error(isPopShop ? '请选择有效期类型' : '请先通过 putKey 查询有效期信息');
        return;
      }
      if (
        values.validityType === 1 &&
        currentShopPlatformType !== 'TM' &&
        (values.days === undefined || values.days === null)
      ) {
        message.error(isPopShop ? '请输入有效天数' : '请先通过 putKey 获取有效期天数');
        return;
      }
      if (
        (values.validityType === 5 || currentShopPlatformType === 'TM') &&
        (!values.validityRange || values.validityRange.length !== 2)
      ) {
        message.error(isPopShop ? '请选择有效期时间段' : '请先通过 putKey 获取有效期时间段');
        return;
      }
      if (values.bindType === 2 && !values.bindSkuIds) {
        message.error('请输入指定商品SKU');
        return;
      }
      if (
        (isPopShop || currentShopPlatformType === 'TM') &&
        couponType === 1 &&
        (values.mainQuota === undefined || values.mainQuota === null || values.mainQuota === '')
      ) {
        message.error('请输入门槛金额');
        return;
      }
      if (values.takeRule === 3 && (values.takeNum === undefined || values.takeNum === null || values.takeNum === '')) {
        message.error('请输入限领张数');
        return;
      }
      if (isPopShop || currentShopPlatformType === 'TM') {
        if (!values.mainDiscount) {
          message.error('请输入优惠金额');
          return;
        }
        if (!values.allCount) {
          message.error('请输入发放数量');
          return;
        }
        if (
          values.takeRule === 3 &&
          (values.takeNum === undefined || values.takeNum === null || values.takeNum === '')
        ) {
          message.error('请输入限领张数');
          return;
        }
      }
      if (isEmptyHtml(detailHtml)) {
        message.error('请输入使用详情');
        return;
      }

      const takeBeginTime = (values.takeTime as Dayjs[])[0]?.format('YYYY-MM-DD HH:mm:ss');
      const takeEndTime = (values.takeTime as Dayjs[])[1]?.format('YYYY-MM-DD HH:mm:ss');

      const validityRange = values.validityRange as Dayjs[] | undefined;
      console.log('validityRange', validityRange);
      let payload: any = {};
      if (currentShopPlatformType === 'TM') {
        payload = {
          shopId: currentShopId,
          style: 4,
          name: values.name,
          platformType: currentShopPlatformType,
          mainQuota: couponType === 1 ? values.mainQuota : undefined,
          mainDiscount: values.mainDiscount,
          allCount: values.allCount,
          takeRule: values.takeRule ?? 0,
          takeNum: values.takeRule === 3 ? values.takeNum : undefined,
          bindType: values.bindType,
          bindSkuIds: values.bindType === 2 ? values.bindSkuIds : undefined,
          thumbnail: values.thumbnail,
          mainImage: values.mainImage,
          detail: detailHtml,
          activityId: values.activityId,
          takeBeginTime,
          takeEndTime,
          validityType: 5,
          beginTime: validityRange?.[0] ? validityRange[0].format('YYYY-MM-DD HH:mm:ss') : undefined,
          endTime: validityRange?.[1] ? validityRange[1].format('YYYY-MM-DD HH:mm:ss') : undefined,
        };
      } else {
        payload = isPopShop
          ? {
              shopId: currentShopId,
              platformType: currentShopPlatformType,
              name: values.name,
              style: 1,
              bindType: values.bindType,
              bindSkuIds: values.bindType === 2 ? values.bindSkuIds : undefined,
              takeBeginTime,
              takeEndTime,
              validityType: values.validityType,
              beginTime:
                values.validityType === 5 && validityRange?.[0]
                  ? validityRange[0].format('YYYY-MM-DD HH:mm:ss')
                  : undefined,
              endTime:
                values.validityType === 5 && validityRange?.[1]
                  ? validityRange[1].format('YYYY-MM-DD HH:mm:ss')
                  : undefined,
              days: values.validityType === 1 ? values.days : undefined,
              mainDiscount: values.mainDiscount,
              mainQuota: couponType === 1 ? values.mainQuota : undefined,
              allCount: values.allCount,
              takeRule: values.takeRule ?? 0,
              takeNum: values.takeRule === 3 ? values.takeNum : undefined,
              type: couponType ?? 1,
              thumbnail: values.thumbnail,
              mainImage: values.mainImage,
              detail: detailHtml,
            }
          : {
              shopId: currentShopId,
              platformType: currentShopPlatformType,
              name: values.name,
              style: 1,
              bindType: values.bindType,
              bindSkuIds: values.bindType === 2 ? values.bindSkuIds : undefined,
              takeBeginTime,
              takeEndTime,
              validityType: values.validityType,
              beginTime:
                values.validityType === 5 && validityRange?.[0]
                  ? validityRange[0].format('YYYY-MM-DD HH:mm:ss')
                  : undefined,
              endTime:
                values.validityType === 5 && validityRange?.[1]
                  ? validityRange[1].format('YYYY-MM-DD HH:mm:ss')
                  : undefined,
              days: values.validityType === 1 ? values.days : undefined,
              mainDiscount: values.mainDiscount,
              mainQuota:
                values.mainQuota || values.mainQuota === 0 || values.mainQuota === '0' ? values.mainQuota : undefined,
              allCount: values.allCount,
              type: 1,
              putKey: values.putKey,
              batchKey: values.batchKey,
              thumbnail: values.thumbnail,
              mainImage: values.mainImage,
              takeRule: values.takeRule ?? 0,
              takeNum: values.takeRule === 3 ? values.takeNum : undefined,
              detail: detailHtml,
            };
      }

      const finalPayload = isEdit ? { ...payload, id: editingId, isPop: currentShopIsPop } : payload;
      setSubmitLoading(true);
      const res = isEdit
        ? await updateCouponByIdApi(currentShopId, finalPayload)
        : await savePlatformCouponApi(currentShopId, finalPayload);
      if (res?.code === 200) {
        message.success(isEdit ? '更新成功' : '创建成功');
        handleClose();
        fetchList({ pageNum: 1 });
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const fetchList = async (override?: { pageNum?: number; pageSize?: number }) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const values = queryForm.getFieldsValue();
    const params = {
      shopId: currentShopId,
      couponName: values.couponName || undefined,
      couponId: values.couponId || undefined,
      bindType: values.bindType || undefined,
      status: values.status || undefined,
      style: values.style || undefined,
      createTimeBegin: values.createTime?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
      createTimeEnd: values.createTime?.[1]?.format('YYYY-MM-DD HH:mm:ss'),
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    };
    setTableLoading(true);
    try {
      const res = await queryCouponListApi(params);
      if (res?.code === 200) {
        setDataSource(res.rows || res.data?.rows || []);
        setTotal(res.total || res.data?.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleEdit = async (record: any) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    try {
      const res = await getCouponDetailApi(record.id);
      if (res?.code === 200 && res.data) {
        applyDetailToForm(res.data || {}, record, { isCopy: false });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCopy = async (record: any) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    try {
      const res = await getCouponDetailApi(record.id);
      if (res?.code === 200 && res.data) {
        applyDetailToForm(res.data || {}, record, { isCopy: true });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSend = (record: any) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    setSendTarget(record);
    setSendAvailableNum(null);
    sendForm.resetFields();
    setSendVisible(true);
    (async () => {
      try {
        setReportLoading(true);
        const res = await getCouponReportApi({ id: record.id, shopId: currentShopId });
        if (res?.code === 200 && res.data) {
          const available = res.data.notUsedCount;
          setSendAvailableNum(typeof available === 'number' ? available : null);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setReportLoading(false);
      }
    })();
  };

  const handleSendSubmit = async () => {
    try {
      const values = await sendForm.validateFields();
      const rawIds = (values.userIdStr || '')
        .split('\n')
        .map((item: string) => item.trim())
        .filter(Boolean);
      if (!rawIds.length) {
        message.error('请输入要发放的用户ID');
        return;
      }
      if (rawIds.length > 1000) {
        message.error('最多输入1000条用户ID');
        return;
      }
      const payload = {
        channel: currentShopPlatformType as string,
        couponId: sendTarget?.couponId,
        reason: values.reason,
        shopId: currentShopId as any,
        userIdStr: rawIds.join(','),
      };
      setSendLoading(true);
      const res = await sendBatchCouponApi(payload);
      if (res?.code === 200) {
        message.success('发券成功');
        setSendVisible(false);
        setSendTarget(null);
        sendForm.resetFields();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    } finally {
      setSendLoading(false);
    }
  };

  const applyDetailToForm = (info: any, record: any, options?: { isCopy?: boolean }) => {
    const takeTime =
      info.takeBeginTime && info.takeEndTime ? [dayjs(info.takeBeginTime), dayjs(info.takeEndTime)] : undefined;
    const validityRange = info.beginTime && info.endTime ? [dayjs(info.beginTime), dayjs(info.endTime)] : undefined;
    const validityTypeValueRaw =
      info.validityType !== undefined && info.validityType !== null ? info.validityType : info.expireType;
    const validityTypeValue =
      validityTypeValueRaw !== undefined && validityTypeValueRaw !== null ? Number(validityTypeValueRaw) : undefined;
    const takeRuleValue =
      info.takeRule !== undefined && info.takeRule !== null ? Number(info.takeRule) : info.takeNum ? 3 : 0;
    const fields = {
      activityId: info.couponId,
      name: info.couponName || info.name,
      style: info.style || 1,
      bindType: info.bindType ?? 1,
      bindSkuIds: info.bindSkuIds,
      takeTime,
      validityType: validityTypeValue,
      days: validityTypeValue === 1 ? (info.days ?? info.addDays) : undefined,
      validityRange: validityTypeValue === 5 ? validityRange : undefined,
      mainQuota: info.quota,
      mainDiscount: info.discount,
      allCount: info.allCount,
      takeRule: takeRuleValue,
      takeNum: takeRuleValue ? info.takeNum : undefined,
      putKey: info.putKey,
      batchKey: info.batchKey,
      thumbnail: info.thumbnail,
      mainImage: info.mainImage,
      type: isPopShop ? (info.type ?? 1) : 1,
    };
    form.resetFields();
    setDetailHtml(info.detail || EMPTY_HTML);
    setEditorKey(prev => prev + 1);
    setEditingId(options?.isCopy ? null : info.id || record.id);
    setDrawerVisible(true);
    setTimeout(() => {
      form.setFieldsValue(fields);
    }, 0);
  };

  const handleViewReport = async (record: any) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    setReportTitle(`${record.couponName || '--'}-${record.couponId || '--'}`);
    setReportVisible(true);
    setReportLoading(true);
    try {
      const res = await getCouponReportApi({ id: record.id, shopId: currentShopId });
      if (res?.code === 200) {
        setReportData(res.data || {});
      }
    } catch (error) {
      console.error(error);
    } finally {
      setReportLoading(false);
    }
  };

  const statusMap: Record<number, string> = {
    1: '未开始',
    2: '进行中',
    3: '已结束',
  };

  const styleMap: Record<number, string> = {
    1: '满减',
    3: '满折',
  };

  const columns: any = [
    { title: '名称', dataIndex: 'couponName', width: 200 },
    { title: '平台优惠券编号', dataIndex: 'couponId', width: 150 },
    {
      title: '优惠金额/比例',
      dataIndex: 'discount',
      width: 150,
      render: (v: any, record: any) => (record.style === 3 ? `${v || '--'}` : v ? `${v}元` : '--'),
    },
    {
      title: '消费门槛',
      dataIndex: 'quota',
      width: 100,
      render: (v: any) => (v ? `${v}元` : '无门槛限制'),
    },
    {
      title: '优惠类型',
      dataIndex: 'style',
      width: 100,
      render: (v: any) => styleMap[v] || '--',
    },
    {
      title: '适用范围',
      dataIndex: 'bindType',
      width: 100,
      render: (v: any) => (v === 2 ? '部分商品' : '全店'),
    },
    {
      title: '平台ID',
      width: 120,
      render: () => currentShopId || '--',
    },
    { title: '创建时间', dataIndex: 'createTime', width: 150 },
    {
      title: '有效期',
      width: 200,
      render: (_: any, record: any) => {
        if (record.beginTime && record.endTime) {
          return `${record.beginTime} 至 ${record.endTime}`;
        }
        if (record.days !== undefined && record.days !== null) {
          return `领取后${record.days}天`;
        }
        return '--';
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (v: number) => (
        <Tag color={v === 2 ? 'blue' : v === 3 ? 'default' : 'orange'}>{statusMap[v] || '--'}</Tag>
      ),
    },
    {
      title: '报告',
      width: 100,
      render: (_: any, record: any) =>
        checkPermissions(COUPON_KEY.REPORT) ? (
          <Button
            type="link"
            size="small"
            onClick={() => handleViewReport(record)}>
            查看报告
          </Button>
        ) : null,
    },
    {
      title: '操作',
      fixed: 'right',
      width: '200px',
      render: (_: any, record: any) => (
        <Space size={15}>
          {checkPermissions(COUPON_KEY.SEND) && (
            <Button
              style={{ padding: 0 }}
              type="link"
              disabled={record.status === 1 || record.status === 3}
              onClick={() => handleSend(record)}>
              发券
            </Button>
          )}
          {checkPermissions(COUPON_KEY.UPDATE) && (
            <Button
              style={{ padding: 0 }}
              type="link"
              disabled={record.status === 3}
              onClick={() => handleEdit(record)}>
              编辑
            </Button>
          )}
          <Button
            style={{ padding: 0 }}
            type="link"
            onClick={() => handleCopy(record)}>
            复制
          </Button>
          {checkPermissions(COUPON_KEY.DELETE) && (
            <Button
              style={{ padding: 0 }}
              type="link"
              danger
              onClick={() => {
                Modal.confirm({
                  title: '确认删除该优惠券？',
                  okButtonProps: { danger: true },
                  onOk: async () => {
                    try {
                      const res = await deleteCouponApi(record.id);
                      if (res?.code === 200) {
                        message.success('删除成功');
                        fetchList({ pageNum: 1 });
                      }
                    } catch (error) {
                      console.error(error);
                    }
                  },
                });
              }}>
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <Form
        layout="inline"
        form={queryForm}
        className={styles.filterForm}>
        <Form.Item
          label="名称"
          name="couponName">
          <Input
            placeholder="请输入"
            allowClear
            className={styles.filterInput}
          />
        </Form.Item>
        <Form.Item
          label="适用范围"
          name="bindType"
          initialValue="">
          <Select
            allowClear
            style={{ width: 200 }}
            options={[
              { label: '全部', value: '' },
              { label: '全店', value: 1 },
              { label: '部分商品', value: 2 },
            ]}
          />
        </Form.Item>
        <Form.Item
          label="平台优惠券编号"
          name="couponId">
          <Input
            placeholder="请输入"
            allowClear
            className={styles.filterInput}
          />
        </Form.Item>
        <Form.Item
          label="状态"
          name="status"
          initialValue="">
          <Select
            allowClear
            style={{ width: 200 }}
            options={[
              { label: '全部', value: '' },
              { label: '未开始', value: 1 },
              { label: '进行中', value: 2 },
              { label: '已结束', value: 3 },
            ]}
          />
        </Form.Item>
        <Form.Item
          label="优惠类型"
          name="style"
          initialValue="">
          <Select
            allowClear
            style={{ width: 200 }}
            options={[
              { label: '全部', value: '' },
              { label: '满减券', value: 1 },
              { label: '满折券', value: 3 },
            ]}
          />
        </Form.Item>
        <Form.Item
          label="创建时间"
          name="createTime">
          <RangePicker
            showTime={{
              defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
            }}
            className={styles.filterRange}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchList({ pageNum: 1 })}>
              查询
            </Button>
            <Button
              onClick={() => {
                queryForm.resetFields();
                fetchList({ pageNum: 1 });
              }}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <div className={styles.tableToolbar}>
        {checkPermissions(COUPON_KEY.ADD) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openDrawer}>
            新建优惠券
          </Button>
        )}
      </div>
      <Table
        rowKey={record => record.id || `${record.couponId}-${record.createTime}`}
        loading={tableLoading}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (pn, ps) => fetchList({ pageNum: pn, pageSize: ps }),
        }}
        scroll={{ x: 1300 }}
      />

      <Drawer
        title={isEdit ? '编辑优惠券' : '新建优惠券'}
        width={980}
        open={drawerVisible}
        destroyOnHidden
        onClose={() => {
          handleClose();
          if (editor) {
            editor.destroy();
            setEditor(null);
          }
        }}
        className={styles.drawer}
        footer={
          <div className={styles.drawerFooter}>
            <Space>
              <Button onClick={handleClose}>取消</Button>
              <Button
                type="primary"
                loading={submitLoading}
                onClick={handleSubmit}>
                保存
              </Button>
            </Space>
          </div>
        }>
        <Form
          layout="horizontal"
          labelCol={{ style: { width: 120 } }}
          wrapperCol={{ style: { flex: 1 } }}
          form={form}
          className={styles.form}>
          {currentShopPlatformType === 'TM' && (
            <>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="优惠券名称"
                    name="name"
                    rules={[{ required: true, message: '请输入优惠券名称' }]}>
                    <Input
                      placeholder="请输入"
                      disabled={isFieldDisabled('name')}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="优惠券类型"
                    name="style"
                    required
                    initialValue={4}
                    rules={[{ required: true, message: '请选择优惠券类型' }]}>
                    <Radio.Group disabled={isFieldDisabled('style')}>
                      <Radio value={4}>会员专享券</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="领券时间"
                    name="takeTime"
                    rules={[{ required: true, message: '请选择领取时间' }]}>
                    <RangePicker
                      showTime
                      className={styles.fullWidth}
                      format="YYYY-MM-DD HH:mm:ss"
                      disabled={isFieldDisabled('takeTime')}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name="validityRange"
                label="有效期"
                rules={[{ required: true, message: '请选择有效期' }]}>
                <RangePicker
                  showTime
                  className={styles.fullWidth}
                  format="YYYY-MM-DD HH:mm:ss"
                  disabled={isEdit}
                />
              </Form.Item>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="消费门槛"
                    style={{ marginBottom: 0 }}
                    required>
                    <Form.Item
                      name="type"
                      initialValue={1}>
                      <Radio.Group
                        className={styles.inlineFlex}
                        disabled={isFieldDisabled('type')}
                        onChange={e => {
                          const val = e.target.value;
                          if (val === 0) {
                            form.setFieldsValue({ mainQuota: undefined });
                          }
                        }}>
                        <Radio value={1}>满</Radio>
                        <Form.Item
                          name="mainQuota"
                          noStyle
                          rules={[{ required: true, message: '请输入门槛金额' }]}>
                          <Input
                            disabled={isFieldDisabled('type') || couponType === 0}
                            placeholder="请输入"
                            addonAfter="元"
                            style={{ width: 200, margin: '0 8px' }}
                          />
                        </Form.Item>
                        <Radio value={0}>无门槛限制</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="优惠金额"
                    name="mainDiscount"
                    rules={[{ required: true, message: '请输入优惠金额' }]}>
                    <Input
                      placeholder="请输入"
                      addonAfter="元"
                      disabled={isFieldDisabled('mainDiscount')}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="发放数量（张）"
                    name="allCount"
                    rules={[{ required: true, message: '请输入发放数量' }]}>
                    <Input
                      placeholder="请输入"
                      addonAfter="张"
                      disabled={isFieldDisabled('allCount')}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label="每人限领"
                required>
                <Form.Item
                  name="takeRule"
                  noStyle
                  initialValue={0}>
                  <Radio.Group
                    className={styles.inlineFlex}
                    disabled={isFieldDisabled('takeRule')}
                    onChange={e => {
                      const val = e.target.value;
                      if (val !== 3) {
                        form.setFieldsValue({ takeNum: undefined });
                      }
                    }}>
                    <Radio value={3}>限制为</Radio>
                    {takeRule && takeRule === 3 ? (
                      <>
                        <Form.Item
                          name="takeNum"
                          noStyle
                          rules={[{ required: true, message: '请输入限领张数' }]}>
                          <Input
                            placeholder="请输入"
                            addonAfter="张"
                            style={{ width: 140, margin: '0 8px' }}
                            disabled={isFieldDisabled('takeNum')}
                          />
                        </Form.Item>
                        <span>张</span>
                      </>
                    ) : null}
                    <Radio value={0}>不限制</Radio>
                  </Radio.Group>
                </Form.Item>
              </Form.Item>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="优惠范围"
                    name="bindType"
                    required
                    rules={[{ required: true, message: '请选择优惠范围' }]}
                    initialValue={1}>
                    <Radio.Group disabled={isFieldDisabled('bindType')}>
                      <Radio value={1}>全店商品</Radio>
                      <Radio value={2}>部分商品</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                {bindType === 2 ? (
                  <Col span={24}>
                    <Form.Item
                      label="指定商品SKU"
                      name="bindSkuIds"
                      rules={[{ required: true, message: '请输入指定商品SKU' }]}>
                      <Input
                        placeholder="请输入SKU，多个以逗号分隔"
                        disabled={isFieldDisabled('bindSkuIds')}
                      />
                    </Form.Item>
                  </Col>
                ) : null}
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    label="activityId"
                    name="activityId"
                    rules={[{ required: true, message: '请输入activityId' }]}>
                    <Input
                      placeholder="请输入"
                      disabled={isFieldDisabled('activityId')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {currentShopPlatformType !== 'TM' && (
            <>
              {isPopShop ? (
                <>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        label="优惠券名称"
                        name="name"
                        rules={[{ required: true, message: '请输入优惠券名称' }]}>
                        <Input
                          placeholder="请输入"
                          disabled={isFieldDisabled('name')}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label="优惠券类型"
                        name="style"
                        required
                        initialValue={1}
                        rules={[{ required: true, message: '请选择优惠券类型' }]}>
                        <Radio.Group disabled={isFieldDisabled('style')}>
                          <Radio value={1}>满减券</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Card
                    className={infoCardClassName}
                    bordered>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          label="领券时间"
                          name="takeTime"
                          rules={[{ required: true, message: '请选择领取时间' }]}>
                          <RangePicker
                            showTime
                            className={styles.fullWidth}
                            format="YYYY-MM-DD HH:mm:ss"
                            disabled={isFieldDisabled('takeTime')}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      label="有效期"
                      name="validityType"
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: '请选择有效期类型' }]}>
                      <Radio.Group disabled={isFieldDisabled('validityType')}>
                        <Radio value={1}>领取后N天有效</Radio>
                        <Radio value={5}>指定时间段</Radio>
                      </Radio.Group>
                    </Form.Item>
                    {validityType === 1 ? (
                      <Form.Item
                        label=" "
                        colon={false}
                        style={{ marginTop: 8 }}>
                        <Space align="center">
                          <Form.Item
                            name="days"
                            noStyle
                            rules={[{ required: true, message: '请输入有效天数' }]}>
                            <Input
                              placeholder="请输入天数"
                              addonAfter="天"
                              style={{ width: 200 }}
                              disabled={isFieldDisabled('days')}
                            />
                          </Form.Item>
                        </Space>
                      </Form.Item>
                    ) : null}
                    {validityType === 5 ? (
                      <Form.Item
                        label=" "
                        colon={false}
                        style={{ marginTop: 8 }}>
                        <Form.Item
                          name="validityRange"
                          noStyle
                          rules={[{ required: true, message: '请选择有效期' }]}>
                          <RangePicker
                            showTime
                            className={styles.fullWidth}
                            format="YYYY-MM-DD HH:mm:ss"
                            disabled={isFieldDisabled('validityRange')}
                          />
                        </Form.Item>
                      </Form.Item>
                    ) : null}
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          label="消费门槛"
                          style={{ marginBottom: 0 }}
                          required>
                          <Form.Item
                            name="type"
                            initialValue={1}>
                            <Radio.Group
                              className={styles.inlineFlex}
                              disabled={isFieldDisabled('type')}
                              onChange={e => {
                                const val = e.target.value;
                                if (val === 0) {
                                  form.setFieldsValue({ mainQuota: undefined });
                                }
                              }}>
                              <Radio value={1}>满</Radio>
                              <Form.Item
                                name="mainQuota"
                                noStyle
                                rules={[{ required: true, message: '请输入门槛金额' }]}>
                                <Input
                                  disabled={isFieldDisabled('type') || couponType === 0}
                                  placeholder="请输入"
                                  addonAfter="元"
                                  style={{ width: 200, margin: '0 8px' }}
                                />
                              </Form.Item>
                              <Radio value={0}>无门槛限制</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="优惠金额"
                          name="mainDiscount"
                          rules={[{ required: true, message: '请输入优惠金额' }]}>
                          <Input
                            placeholder="请输入"
                            addonAfter="元"
                            disabled={isFieldDisabled('mainDiscount')}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="发放数量（张）"
                          name="allCount"
                          rules={[{ required: true, message: '请输入发放数量' }]}>
                          <Input
                            placeholder="请输入"
                            addonAfter="张"
                            disabled={isFieldDisabled('allCount')}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      label="每人限领"
                      required>
                      <Form.Item
                        name="takeRule"
                        noStyle
                        initialValue={0}>
                        <Radio.Group
                          className={styles.inlineFlex}
                          disabled={isFieldDisabled('takeRule')}
                          onChange={e => {
                            const val = e.target.value;
                            if (val !== 3) {
                              form.setFieldsValue({ takeNum: undefined });
                            }
                          }}>
                          <Radio value={3}>限制为</Radio>
                          {takeRule && takeRule === 3 ? (
                            <>
                              <Form.Item
                                name="takeNum"
                                noStyle
                                rules={[{ required: true, message: '请输入限领张数' }]}>
                                <Input
                                  placeholder="请输入"
                                  addonAfter="张"
                                  style={{ width: 140, margin: '0 8px' }}
                                  disabled={isFieldDisabled('takeNum')}
                                />
                              </Form.Item>
                              <span>张</span>
                            </>
                          ) : null}
                          <Radio value={0}>不限制</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Form.Item>
                  </Card>

                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        label="优惠范围"
                        name="bindType"
                        required
                        rules={[{ required: true, message: '请选择优惠范围' }]}
                        initialValue={1}>
                        <Radio.Group disabled={isFieldDisabled('bindType')}>
                          <Radio value={1}>全店商品</Radio>
                          <Radio value={2}>部分商品</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                    {bindType === 2 ? (
                      <Col span={24}>
                        <Form.Item
                          label="指定商品SKU"
                          name="bindSkuIds"
                          rules={[{ required: true, message: '请输入指定商品SKU' }]}>
                          <Input
                            placeholder="请输入SKU，多个以逗号分隔"
                            disabled={isFieldDisabled('bindSkuIds')}
                          />
                        </Form.Item>
                      </Col>
                    ) : null}
                  </Row>
                </>
              ) : (
                <>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        label="优惠券名称"
                        name="name"
                        rules={[{ required: true, message: '请输入优惠券名称' }]}>
                        <Input
                          placeholder="请输入"
                          disabled={isFieldDisabled('name')}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        label="优惠券类型"
                        name="style"
                        required
                        initialValue={1}
                        rules={[{ required: true, message: '请选择优惠券类型' }]}>
                        <Radio.Group disabled={isFieldDisabled('style')}>
                          <Radio value={1}>满减券</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        label="优惠范围"
                        name="bindType"
                        required
                        rules={[{ required: true, message: '请选择优惠范围' }]}
                        initialValue={1}>
                        <Radio.Group disabled={isFieldDisabled('bindType')}>
                          <Radio value={1}>全店商品</Radio>
                          <Radio value={2}>部分商品</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                    {bindType === 2 ? (
                      <Col span={24}>
                        <Form.Item
                          label="指定商品SKU"
                          name="bindSkuIds"
                          rules={[{ required: true, message: '请输入指定商品SKU' }]}>
                          <Input
                            placeholder="请输入SKU，多个以逗号分隔"
                            disabled={isFieldDisabled('bindSkuIds')}
                          />
                        </Form.Item>
                      </Col>
                    ) : null}
                  </Row>
                  <Form.Item
                    label="每人限领"
                    required
                    extra={
                      <span style={{ color: '#ff4d4f' }}>
                        注意：优惠券类型、优惠范围、每人限领张数，请按所申请优惠券的实际信息填写
                      </span>
                    }>
                    <Form.Item
                      name="takeRule"
                      noStyle
                      initialValue={0}>
                      <Radio.Group
                        className={styles.inlineFlex}
                        disabled={isFieldDisabled('takeRule')}
                        onChange={e => {
                          const val = e.target.value;
                          if (val !== 3) {
                            form.setFieldsValue({ takeNum: undefined });
                          }
                        }}>
                        <Radio value={3}>限制为</Radio>
                        {takeRule && takeRule === 3 ? (
                          <>
                            <Form.Item
                              name="takeNum"
                              noStyle
                              rules={[{ required: true, message: '请输入限领张数' }]}>
                              <Input
                                placeholder="请输入"
                                addonAfter="张"
                                style={{ width: 140, margin: '0 8px' }}
                                disabled={isFieldDisabled('takeNum')}
                              />
                            </Form.Item>
                            <span>张</span>
                          </>
                        ) : null}
                        <Radio value={0}>不限制</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Form.Item>
                  <Form.Item
                    label="putKey"
                    extra="需通过京东平台采销，创建“免费抢券”类优惠券"
                    className={styles.putKeyRow}>
                    <Space
                      size={12}
                      wrap>
                      <Form.Item
                        name="putKey"
                        noStyle
                        rules={[{ required: true, message: '请输入 putKey' }]}>
                        <Input
                          placeholder="请输入"
                          disabled={isEdit}
                          className={styles.putKeyInput}
                        />
                      </Form.Item>
                      <Button
                        icon={<SearchOutlined />}
                        disabled={isEdit}
                        loading={queryLoading}
                        onClick={handleFetchCouponInfo}>
                        查询
                      </Button>
                    </Space>
                  </Form.Item>

                  <Card
                    className={infoCardClassName}
                    bordered>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item
                          label="领券时间"
                          name="takeTime"
                          rules={[{ required: true, message: '请通过putKey获取领取时间' }]}>
                          <RangePicker
                            showTime
                            disabled
                            className={styles.fullWidth}
                            format="YYYY-MM-DD HH:mm:ss"
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item
                      label="有效期"
                      name="validityType"
                      rules={[{ required: true, message: '请通过putKey获取有效期类型' }]}>
                      <Radio.Group disabled>
                        <Radio value={1}>领取后N天有效</Radio>
                        <Radio value={5}>指定时间段</Radio>
                      </Radio.Group>
                    </Form.Item>
                    {validityType === 1 ? (
                      <Form.Item
                        label=" "
                        colon={false}
                        style={{ marginTop: 8 }}>
                        <Space align="center">
                          <Form.Item
                            name="days"
                            noStyle
                            rules={[{ required: true, message: '请通过putKey获取有效天数' }]}>
                            <Input
                              placeholder="请输入天数"
                              disabled
                              style={{ width: 200 }}
                            />
                          </Form.Item>
                          <span>天</span>
                        </Space>
                      </Form.Item>
                    ) : null}
                    {validityType === 5 ? (
                      <Form.Item
                        label=" "
                        colon={false}
                        style={{ marginTop: 8 }}>
                        <Form.Item
                          name="validityRange"
                          noStyle
                          rules={[{ required: true, message: '请通过putKey获取有效期' }]}>
                          <RangePicker
                            showTime
                            disabled
                            className={styles.fullWidth}
                            format="YYYY-MM-DD HH:mm:ss"
                          />
                        </Form.Item>
                      </Form.Item>
                    ) : null}
                    <Form.Item
                      label="消费门槛"
                      required>
                      <Form.Item
                        name="type"
                        noStyle
                        initialValue={1}>
                        <Radio.Group
                          disabled
                          onChange={e => {
                            const val = e.target.value;
                            if (val === 0) {
                              form.setFieldsValue({ mainQuota: undefined });
                            }
                          }}>
                          <Radio value={1}>满</Radio>
                          {couponType === 1 ? (
                            <>
                              <Form.Item
                                name="mainQuota"
                                noStyle
                                rules={[{ required: true, message: '请输入门槛金额' }]}>
                                <Input
                                  placeholder="请输入"
                                  disabled
                                  style={{ width: 200, margin: '0 8px' }}
                                />
                              </Form.Item>
                              <span>元</span>
                            </>
                          ) : null}
                          <Radio value={0}>无门槛限制</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="优惠金额"
                          name="mainDiscount"
                          rules={[{ required: true, message: '请通过putKey获取优惠金额' }]}>
                          <Input
                            addonAfter="元"
                            placeholder="请输入"
                            disabled
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="发放数量（张）"
                          name="allCount"
                          rules={[{ required: true, message: '请通过putKey获取发放数量' }]}>
                          <Input
                            addonAfter="张"
                            placeholder="请输入"
                            disabled
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </>
              )}
            </>
          )}

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="缩略图"
                name="thumbnail"
                rules={[{ required: true, message: '请上传缩略图' }]}>
                <ImageUploader
                  maxSizeKB={2048}
                  onChange={val =>
                    form.setFieldsValue({
                      thumbnail: val?.imageUrl || val,
                    })
                  }
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="主图"
                name="mainImage"
                rules={[{ required: true, message: '请上传主图' }]}>
                <ImageUploader
                  maxSizeKB={2048}
                  onChange={val =>
                    form.setFieldsValue({
                      mainImage: val?.imageUrl || val,
                    })
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="使用详情"
            required>
            {drawerVisible ? (
              <Card
                className={styles.editorCard}
                bordered>
                {editor ? (
                  <Toolbar
                    editor={editor}
                    defaultConfig={toolbarConfig}
                    className={styles.editorToolbar}
                  />
                ) : null}
                <Editor
                  key={editorKey}
                  defaultConfig={editorConfig}
                  value={detailHtml}
                  onCreated={setEditor}
                  onChange={ed => setDetailHtml(ed.getHtml())}
                  className={styles.editor}
                />
              </Card>
            ) : null}
          </Form.Item>
          <Form.Item
            name="batchKey"
            hidden>
            <Input />
          </Form.Item>
          {!isPopShop ? (
            <Form.Item
              name="type"
              hidden
              initialValue={1}>
              <Input />
            </Form.Item>
          ) : null}
        </Form>
      </Drawer>
      <Modal
        width={800}
        title={`${sendTarget?.couponName || '--'}-${sendTarget?.couponId || '--'} 发放优惠券`}
        open={sendVisible}
        onCancel={() => {
          setSendVisible(false);
          setSendTarget(null);
          sendForm.resetFields();
          setSendAvailableNum(null);
        }}
        confirmLoading={sendLoading}
        okText="提交"
        cancelText="取消"
        onOk={handleSendSubmit}>
        <div style={{ marginBottom: 12 }}>
          <span style={{ marginRight: 8 }}>优惠券可分配数量：{sendAvailableNum ?? sendTarget?.num ?? '--'}</span>
          <Text type="secondary">注：发放数量不可大于优惠券可分配数量</Text>
        </div>
        <Form
          layout="vertical"
          form={sendForm}>
          <Form.Item
            label="请输入要发放的平台用户ID，每行一个用户，最多输入1000条"
            name="userIdStr"
            rules={[{ required: true, message: '请输入用户ID' }]}>
            <Input.TextArea
              rows={6}
              placeholder="请输入，每行一个用户ID"
            />
          </Form.Item>
          <Form.Item
            label="发放理由"
            name="reason"
            rules={[{ required: true, message: '请输入发放理由' }]}>
            <Input placeholder="请输入" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={reportTitle || '优惠券报告'}
        open={reportVisible}
        confirmLoading={reportLoading}
        footer={null}
        onCancel={() => {
          setReportVisible(false);
          setReportData(null);
        }}>
        <Spin spinning={reportLoading}>
          {reportData ? (
            <Space
              direction="vertical"
              size={16}
              style={{ width: '100%' }}>
              <div>优惠券总量：{reportData.totalCount ?? '--'}</div>
              <div>锁定数量：{reportData.usedCount ?? '--'}</div>
              <div>可分配数量：{reportData.notUsedCount ?? '--'}</div>
              <div>发放量：{reportData.sendCount ?? '--'}</div>
              <div>
                发放比例：
                {reportData.sendRate !== undefined && reportData.sendRate !== null
                  ? `${(
                      (Number(reportData.sendRate) <= 1
                        ? Number(reportData.sendRate) * 100
                        : Number(reportData.sendRate)) || 0
                    )
                      .toFixed(2)
                      .replace(/\.?0+$/, '')}%`
                  : '--'}
              </div>
            </Space>
          ) : (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>暂无数据</div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default StoreCoupon;
